import { type ClassValue, clsx} from 'clsx';
import React, { type FC, type MutableRefObject, type ReactNode } from 'react';

import { type ElementProps, Element, Div, Span } from '/react4xp/components/Element';
import { TooltipIcon } from '/react4xp/components/TooltipIcon';
import { customTwMerge } from '/react4xp/util/customTwMerge';
import { PADDING_BOTTOM } from '/react4xp/tailwind/padding';

export const Legend: FC<Omit<ElementProps,'as'> & {
	disabled?: boolean
}> = ({
	children,
	disabled,
	color = disabled ? 'textNeutralDisabled' : undefined,
	// @ts-expect-error as is Omittted in the type, but one could still pass it...
	as: _overridden1, // Make sure as doesn't end up in otherProps.
	...otherProps
}): JSX.Element | null => children ? <Element
	{...otherProps}
	as='legend'
	color={color}
>
	{children}
</Element> : null;


interface FieldsetProps extends Omit<ElementProps,'as'> {
	borderless?: boolean;
	disabled?: boolean;

	// ERROR: Since the form is always controlled and
	//        needs a context this isn't a good idea!
	// form?: string;

	legend?: ReactNode;
	legendClassName?: ClassValue;
	legendId?: string;
	legendRef?: MutableRefObject<HTMLLegendElement | undefined>
	legendTooltipHtml?: string;
	name?: string;
	required?: boolean;
}

const fieldSetProps = ({
	borderless = false,

	// Must come before border, borderColor and borderRadius.
	borderStyle = borderless
		? 'none' // Override user-agent stylesheet
		: undefined,

	disabled, // Must come before borderColor.

	borderWidth = (borderStyle && ['none', 'hidden'].includes(borderStyle)) ? undefined : 1,
	borderColor = (borderStyle && ['none', 'hidden'].includes(borderStyle))
		? undefined
		: disabled
			? 'textNeutralDisabled'
			: 'neutralSubtle',
	borderRadius = (borderStyle && ['none', 'hidden'].includes(borderStyle)) ? undefined : 'md',
	marginX = borderless
		? 0 // Override user-agent stylesheet
		: undefined,
	padding = borderless ? {
		all: 0, // Override user-agent stylesheet
	}: {
		block: 'lg',
		inline: 'md',
	},
	tablet = {
		padding: borderless ? undefined : { all: 'xxl' },
	},
	// @ts-expect-error as is Omittted in the type, but one could still pass it...
	as: _overridden1, // Make sure as doesn't end up in otherProps.
	...otherProps
}: FieldsetProps): Record<string, unknown> => ({
	...otherProps,
	as: 'fieldset',
	borderColor,
	borderRadius,
	borderStyle,
	borderWidth,
	disabled, // Not a generic html attribute, only for <fieldset>
	padding,
	tablet,
});


export const Fieldset: FC<FieldsetProps> = ({
	children,
	disabled,
	display = 'flex',

	// TODO this only makes it possible to override gap on mobile,
	// should probably use className for that instead.
	gap = ['flex','grid'].includes(display) ? 'xxs' : undefined,

	flexDirection = display === 'flex' ? 'col' : undefined,
	legend,
	legendClassName,
	legendId,
	legendRef,
	legendTooltipHtml,
	required,
	...otherProps
}) => {
	if (!children) {
		return null;
	}

	return (
		<Element
			{...fieldSetProps({...otherProps, disabled, legendRef: undefined})}
		>
			{
				legend
					? <Legend
						className={customTwMerge(
							'font-semibold text-xl tablet:pb-xs',
							gap ? PADDING_BOTTOM[gap] : undefined,
							legendClassName,
						)}
						disabled={disabled}
						elementRef={legendRef}
						id={legendId}
					>
						{legend}
						{
							required
								? <>
									&nbsp;
									<Span color={disabled ? 'textNeutralDisabled' : 'iconFeedbackErrorDefault'}>
										*
									</Span>
								</>
								: null
						}
						{
							legendTooltipHtml
								? <>
									&nbsp;
									<TooltipIcon html={legendTooltipHtml}/>
								</>
								: null
						}
					</Legend>
					: null
			}
			<Div
				className='tablet:gap-xs'
				display={display}
				flexDirection={flexDirection}
				gap={gap}
			>
				{children}
			</Div>
		</Element>
	);
};
